import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { utils, write } from "xlsx";
import Papa from "papaparse";
import moment from "moment";
import { Menu } from 'primereact/menu';

import { DropDownField, TextInputField, MultiSelectField, AutoCompleteField } from "../../../helpers";
import Button from "../../../helpers/Button";
import DateFilter from "../../../common/DateFilter";

import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { fetchSellerSearch, updateSearchParams, updateSearchOptions, setSearchConfig, setIsSearchByLocked } from "../../../services/redux/reducers/sellerSearch/sellerSearch";
import { fetchBuyerCompanies, fetchMaterialCategories } from "../../../services/redux/reducers/commonThunk";
import { getSellerSearch } from "../../../services/api/sellerSearch";
import { getStructuredProductMaterialSpecification } from "../../../services/api/materialSpecifications";

import { getAddFilterOptions, getAutoSearchSuggestions, getDateRange, getMatchedRegexPattern, normalizeLabel, useFuzzSearch } from "../../../utils";

import StructuredProductSearch from "../../../common/StructuredProductSearch";
import StructuredMaterialDisplay from "../../../common/StructuredMaterialDisplay";
import Stepper from "../../../helpers/Stepper";
import Loading from "../../../common/Loading";

import { Dialog } from "primereact/dialog";

import { validateInputs } from "../../../utils";
import { StructuredProductFilterValidationSchema, StructuredProductFilterInitialState } from "../../../validation/structuredProductFilterValidation";


const sellerTransactionSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Seller Transactions', value: 'sellerTransactionData' }
        ]
    },
    {
        srNO: 2,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",
    },
    {
        srNO: 3,
        static: true,
        type: "DATE",
        name: "dateType",
        label: "Date",
        options: [
            { label: 'Created Date', value: 'createdAt' }
        ]
    },
    {
        srNO: 4,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "materialId",
        label: "Material Id",
    },
    {
        srNO: 5,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "docNo",
        label: "seller PO No",
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "brand",
        label: "Brand Name",
    },
    {
        srNO: 7,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "prodName",
        label: "Product Name",
    },
    {
        srNO: 8,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "variantName",
        label: "Variant Name",
    },
    {
        srNO: 9,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "prodSpec",
        label: "Product Specification",
    },
    {
        srNO: 10,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "AUTO_COMPLETE",
        name: "companyName",
        label: "Buyer Company",
        priority: 1
    },
    {
        srNO: 11,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerCompanyName",
        label: "Seller Company",
    },
    {
        srNO: 12,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "category",
        label: "Category",
    },
    {
        srNO: 13,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "companyERPCode",
        label: "Company ERP Code"
    },
    {
        srNO: 14,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "MULTISELECT",
        name: "pmBranchId",
        label: "PM Branch",
    },
    {
        srNO: 15,
        static: false,
        removable: true,
        isSearchByOption: false,
        type: "DIALOG",
        name: "strProductName",
        label: "Structured Product",
    }
]

const docNoSearchLabels = ["docNo", "fileNo"]


const SearchBar = (props) => {

    const dispatch = useDispatch()

    const [suggestions, setSuggestions] = useState([])
    const [isStructuredProductSearchVisible, setIsStructuredProductSearchVisible] = useState(false)
    const [isSummaryVisible, setIsSummaryVisible] = useState(false)

    const [structuredProductFilter, setStructuredProductFilter] = useState(StructuredProductFilterInitialState)
    const [isDataLoading, setIsDataLoading] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [filteredMaterials, setFilteredMaterials] = useState([])
    const [filteredBrands, setFilteredBrands] = useState([])

    const paginationParams = useSelector(state => state.sellerSearchData.paginationParams)
    const searchParams = useSelector(state => state.sellerSearchData.searchParams)
    const searchOptions = useSelector(state => state.sellerSearchData.searchOptions)
    const isLoading = useSelector(state => state.sellerSearchData.loading)
    const sellerTransactionData = useSelector(state => state.sellerSearchData.sellerTransactionData)
    const brandData = useSelector((state) => state.common.materialBrands)
    const orderTypes = useSelector(state => state.common.orderTypes)
    const userDetails = useSelector(state => state.userDetails)
    const isSearchByLocked = useSelector(state => state.sellerSearchData.isSearchByLocked)
    const companyBranches = userDetails.PMCompanyDetails.companyBranches
    const materialCategories = useSelector((state) => state.common.materialCategories)

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    const searchConfig = sellerTransactionSearchConfig

    let buyerCompanies = useSelector(state => state.common.buyerCompanies)

    if (buyerCompanies.length){
        buyerCompanies = buyerCompanies.map(company => {
            const companyName = company.companyName
            return { ...company, displayName: companyName }
        })
    }

    useEffect(() => {
        dispatch(setSearchConfig(sellerTransactionSearchConfig))
        dispatch(fetchBuyerCompanies())
        dispatch(fetchMaterialCategories())

        if (searchOptions.length === 0) {
            const filteredSearchOptions = searchConfig.filter(eachOption => eachOption.static)
            dispatch(updateSearchOptions(filteredSearchOptions))
        }
    }, [])

    useEffect(() => {
        if (Object.keys(searchParams.structuredProductFilter).length) {
            setIsSummaryVisible(true)
        }
    }, [searchParams.structuredProductFilter])

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.structuredProductFilter])

    const companyFuzzSearch = useFuzzSearch(buyerCompanies, "displayName")

    const addFilterOption = (srNO) => {
        let relatedConfig = searchConfig.find(eachConfig => eachConfig.srNO === srNO)
        if(relatedConfig.name === "pmBranchId"){
            const updatedCompanyBranches = companyBranches.map(eachCompanyBranch => ({...eachCompanyBranch, label:eachCompanyBranch.branchName, value:eachCompanyBranch.branchId}))
            relatedConfig = {...relatedConfig, options: updatedCompanyBranches}
        }
        if (relatedConfig.name === "strProductName") {
            setIsStructuredProductSearchVisible(true)
        }
        dispatch(updateSearchOptions([...searchOptions, { ...relatedConfig }]))
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = { ...searchParams }

        if (relatedSearchOption.type === "SEARCH_BY") {
            updatedSearchParams.searchText = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DATE") {
            updatedSearchParams.fromDate = null
            updatedSearchParams.toDate = null
            updatedSearchParams.range = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "SELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "TEXT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "AUTO_COMPLETE") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "DIALOG") {
            updatedSearchParams[relatedSearchOption.name] = ""
            updatedSearchParams.structuredProductFilter = {}
            setIsSummaryVisible(false)
            dispatch(updateSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value
        console.log("eventValue", eventValue)
        if (eventValue && (eventValue.userId || eventValue.companyId)) {
            eventValue = eventValue.displayName
        }

        const updatedSearchParams = { ...searchParams, [event.target.name]: eventValue }

        if (eventValue instanceof Date) {
            if (new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()) {
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        if (event.target.name === "searchText" && event.target.value.length > 3){
            const availableOptions = [...sellerTransactionSearchConfig]
            const matchText = eventValue.split(";")[0]
            const matchedPattern = getMatchedRegexPattern(matchText, availableOptions, searchParams.searchBy)
            if(!isSearchByLocked){
                updatedSearchParams.searchBy = matchedPattern
            }
        }

        if (event.target.name === "searchBy"){
            dispatch(setIsSearchByLocked(true))
        }

        dispatch(updateSearchParams(updatedSearchParams))
    }

    const handleSearchByLock = () => {
        if(isSearchByLocked && searchParams.searchText === ""){
            const updatedSearchParams = { ...searchParams, searchBy : "all" }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        else if(isSearchByLocked && searchParams.searchText !== ""){
            const availableOptions = [...sellerTransactionSearchConfig]
            const matchedPattern = getMatchedRegexPattern(searchParams.searchText, availableOptions, searchParams.searchBy)
            const updatedSearchParams = { ...searchParams, searchBy : matchedPattern }
            dispatch(updateSearchParams(updatedSearchParams))
        }
        dispatch(setIsSearchByLocked(!isSearchByLocked))
    }

    const handleDateRangeChange = (range) => {
        const { fromDate, toDate } = getDateRange(range)
        getDateRange(range)
        dispatch(updateSearchParams({ ...searchParams, range, fromDate, toDate }))
    }

    const autoCompleteSearch = (searchType, query) => {
        const suggestions = getAutoSearchSuggestions({
            searchType,
            query,
            buyerCompanies,
            companyFuzzSearch
        })

        setSuggestions(suggestions)
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const downloadData = async (dataFormat) => {
        let from_date = searchParams.fromDate
        let to_date = searchParams.toDate
        if (searchParams.fromDate) {
            from_date = moment(searchParams.fromDate, "DD.MM.YYYY").toDate()
        }
        if (searchParams.toDate) {
            to_date = moment(searchParams.toDate, "DD.MM.YYYY").toDate()
        }
        const date_difference = moment(new Date(to_date)).diff(moment(new Date(from_date)), 'days')
        console.log("date_difference", date_difference)
        if (date_difference > 60) {
            return dispatch(raiseToast({ severity: 'error', detail: "Please select Date range within 2 monthes", life: 3000 }))
        }

        const updatedSearchParams = { ...searchParams }
        if(updatedSearchParams.searchBy !== "all"){
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        const response = await getSellerSearch({ ...updatedSearchParams, paginator: false })
        let responseData = response.data.data

        responseData = responseData.map(item => {
            const relatedBrand = brandData.find(eachBrand => eachBrand.brandId === item.brandId)
            const relatedOrderType = orderTypes.find((eachOrderType) => eachOrderType.orderTypeId === item.orderTypeId)
            return {
                sellerCompanyName: item.sellerCompanyName,
                sellerBranchName: item.sellerBranchName,
                sellerCity: item.sellerCity,
                sellerPONumber: item.sellerPONumber,
                materialId: item.materialId,
                companyERPCode: item.companyERPCode,
                sellerInvoice: item.sellerInvoice,
                sellerInvoiceDate: item.sellerInvoiceDate,
                productName: item.productName,
                productSpecification: item.productSpecification,
                sellerUnitPrice: item.sellerUnitPrice,
                buyerUnitPrice: item.buyerUnitPrice,
                GST: item.GST,
                buyerCompanyName: item.buyerCompanyName,
                sellerPhoneNum: item.sellerPhoneNum,
                sellerEmailId: item.sellerEmailId,
                buyerPONum: item.buyerPONum,
                brandName: relatedBrand?.brandName,
                orderType: relatedOrderType.orderType,
                isRouted: item.isRouted ? "Routed" : "Not Routed"
            }
        })

        if (dataFormat === "CSV") {
            let csv = Papa.unparse(responseData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "sellerTransactionsData.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if (dataFormat === "XLSX") {
            const ws = utils.json_to_sheet(responseData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'sellerTransactionsData.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]

    const removeStructuredProductFilter = () => {
        const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === "strProductName")
        removeFilterOption(relatedSearchOption.srNO)
    }

    const handleStructuredProductFilter = () => {
        const relatedCategory = materialCategories.find(eachCategory => eachCategory.categoryId === structuredProductFilter.selectedCategoryId)
        setIsStructuredProductSearchVisible(false)
        setIsSummaryVisible(true)
        dispatch(updateSearchParams({ ...searchParams, structuredProductFilter: {...structuredProductFilter, selectedCategory: relatedCategory} }))
    }


    const handleSearch = () => {
        let errorFound = false

        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        }
        else if (searchParams.searchText && searchParams.searchBy !== "all") {
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if (relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])) {
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }

        searchOptions.map(eachOption => {
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)

                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if (eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])) {
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            }
        })

        const updatedSearchParams = { ...searchParams }

        if (updatedSearchParams.searchBy !== "all") {
            updatedSearchParams[updatedSearchParams.searchBy] = updatedSearchParams.searchText.trim()
        }

        const sellerTransactionParams = {
            ...paginationParams,
            ...updatedSearchParams,
            pageNo: 0,
        }

        if (!errorFound) {
            dispatch(fetchSellerSearch(sellerTransactionParams))
        }
    }

    const handleSubmitAddFilterModal = async () => {
        const validation_res = await validateInputs(StructuredProductFilterValidationSchema, structuredProductFilter)
        if(validation_res.error){
            setFormErrors(validation_res.error)
        }
        else{
            setFormErrors({})
            setIsDataLoading(true)
            const response = await getStructuredProductMaterialSpecification(structuredProductFilter)
            setIsDataLoading(false)
            if (response.failure) {
                return dispatch(raiseToast({ severity: 'error', summary: "Add Structured Filter", detail: response.data.error, life: 3000 }))
            }
            setFilteredMaterials(response.data)
            const relatedCategory = materialCategories.find(eachCategory => eachCategory.categoryId === structuredProductFilter.selectedCategoryId)
            dispatch(updateSearchParams({ ...searchParams, structuredProductFilter: {...structuredProductFilter, selectedCategory: relatedCategory} }))
            return true
        }
        
    }

    const steps = [
        {
            header: 'Add Filter',
            content: (
                <div className="p-2" style={{ height: "480px" }}>
                    <StructuredProductSearch
                        initialFilter={searchParams.structuredProductFilter}
                        structuredProductFilter={structuredProductFilter}
                        setStructuredProductFilter={setStructuredProductFilter}
                        formErrors={formErrors}
                        filteredBrands={filteredBrands}
                        setFilteredBrands={setFilteredBrands}
                    />
                </div>
            ),
            onNext: handleSubmitAddFilterModal
        },
        {
            header: 'Material Preview',
            content: (
                <div className="px-3 py-2" style={{ height: "480px"}}>
                    <StructuredMaterialDisplay products={filteredMaterials} />
                </div>
            )
        }
    ]

    return (
        <div className="p-card" style={{ border: "1px solid #e9ecef" }}>
            <Loading visible={isDataLoading} />
            <div className="grid">
                {
                    searchOptions.map(eachOption => {

                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{ label: '', value: 'all' }]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if (!optionInUse) {
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })

                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        {['companyName'].includes(searchParams.searchBy) ? (
                                            <AutoCompleteField
                                                value={searchParams.searchText}
                                                suggestions={suggestions}
                                                searchMethod={(e) => autoCompleteSearch(searchParams.searchBy, e.query)}
                                                field="displayName"
                                                labelText="Search Text"
                                                onChange={handleSearchOnChange}
                                                name="searchText"
                                                dropdown
                                                refName={searchTextRef}
                                            />
                                        ) : (
                                            <TextInputField
                                                name="searchText"
                                                labelText="Search Text"
                                                placeholder="No Filter"
                                                refName={searchTextRef}
                                                value={searchParams["searchText"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        )}
                                    </div>
                                    {(searchParams.searchText || isSearchByLocked)  && (
                                        <>
                                            <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i
                                                className={`pi ${isSearchByLocked ? 'pi-lock' : 'pi-lock-open'}`}
                                                style={{ position: 'absolute', top: '6px', cursor: 'pointer', color: 'orange' }}
                                                onClick={() => handleSearchByLock()}
                                            ></i>
                                            </div>
                                            <div className="flex flex-column col-12">
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                            </div>
                                        </>
                                    )}
                                </>
                            )
                        }

                        if (eachOption.type === "DATE") {
                            return (
                                <>
                                    <DateFilter
                                        disabled={docNoSearchLabels.includes(searchParams.searchBy)}
                                        eachOption={eachOption}
                                        searchParams={searchParams}
                                        dateOptions={["MTD", "PM", "L15", "L30"]}
                                        handleDateRangeChange={handleDateRangeChange}
                                        handleSearchOnChange={handleSearchOnChange}
                                        removeFilterOption={removeFilterOption}
                                    />
                                </>
                            )
                        }
                        if (eachOption.type === "SELECT") {
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({ label: eachSelectOption.label, value: eachSelectOption.value }))
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }
                        if (eachOption.type === "MULTISELECT") {
                            return (
                                <>
                                    {!eachOption.static ?
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null
                                    }
                                    <div className="flex flex-column col-12 custom-multiselect">
                                        <MultiSelectField name={eachOption.name} labelText={eachOption.label} options={eachOption.options} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)}></i>
                                        </div>
                                    }
                                    <div className="flex flex-column col-12">
                                        <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "AUTO_COMPLETE") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <AutoCompleteField
                                                value={searchParams[eachOption.name]}
                                                suggestions={suggestions}
                                                searchMethod={(e) => autoCompleteSearch(eachOption.name, e.query)}
                                                field="displayName"
                                                labelText={eachOption.label}
                                                onChange={handleSearchOnChange}
                                                name={eachOption.name}
                                                dropdown
                                            />
                                        </div>
                                    }
                                </>
                            )
                        }

                        if (eachOption.type === "DIALOG" && isStructuredProductSearchVisible) {
                            return (
                                <>
                                    <Dialog
                                        header="STRUCTURED PRODUCT FILTER"
                                        style={{ height: "70vh", width: "75vw" }}
                                        className='mb-3'
                                        visible={isStructuredProductSearchVisible}
                                        onHide={() => {
                                            setIsStructuredProductSearchVisible(false)
                                            if (!Object.keys(searchParams.structuredProductFilter).length) {
                                                removeFilterOption(eachOption.srNO)
                                            }
                                        }}
                                    >
                                        <Stepper
                                            steps={steps}
                                            onSubmit={handleStructuredProductFilter}
                                        />
                                    </Dialog>
                                </>
                            )
                        }
                    })
                }
                {isSummaryVisible && (
                    <div className="flex flex-column col-12 p-2 border-0 border-gray-300 surface-100 rounded mb-2" style={{ position: 'relative' }}>

                        <div className="flex items-center">
                            <span className="font-semibold text-sm">Structured Filter</span>

                            <div className="flex gap-2 ml-auto" style={{ fontSize: '0.6rem' }}>
                                <Button
                                    icon="pi pi-pencil"
                                    className="p-button-text p-0"
                                    onClick={() => setIsStructuredProductSearchVisible(true)}
                                    aria-label="Edit"
                                />
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-text p-0 text-red-600"
                                    onClick={() => removeStructuredProductFilter()}
                                    aria-label="Remove"
                                />
                            </div>
                        </div>

                        <div className="text-xs text-gray-800 leading-5 mt-2 flex flex-column gap-1">
                            <div className="flex gap-2"><span>Category:</span><strong>{searchParams.structuredProductFilter.selectedCategory.categoryName}</strong> </div>
                            <div className="flex gap-2"><span>Product:</span><strong>{searchParams.structuredProductFilter.selectedProduct.productName}</strong></div>
                            <div className="flex gap-2"><span>Variant:</span> <strong>{searchParams.structuredProductFilter.selectedVariant.variantName}</strong></div>
                            <div className="flex gap-2"><span>Brand:</span> <strong>{searchParams.structuredProductFilter.selectedBrand.brandName}</strong></div>
                            {
                                Object.entries(searchParams.structuredProductFilter.selectedAttributes).length > 0 &&
                                <>
                                    <p className="mt-2"><strong>Attributes:</strong></p>
                                    <ul className="ml-4 list-disc">
                                        {Object.entries(searchParams.structuredProductFilter.selectedAttributes || {}).map(([attr, vals]) => (
                                            <li key={attr}><strong>{attr}:</strong> {vals.join(', ')}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        </div>
                    </div>
                )}

                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0" style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                            <Button label="Search" style={{ flexGrow: 1 }} className="py-2" onClick={handleSearch} disabled={isLoading} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3" onClick={(e) => downloadMenu.current.toggle(e)} disabled={isLoading} permissions={[255]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SearchBar;